import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import SelectDays from "../../../inputs/select-days";
import submit from "submit-form";
import Modal from "../../../modal-new";
import Select from "../../../inputs/select";
import SvgIcon from "../../../SvgIcon";
import ConfirmationModal from "../../ConfirmationModal";

const weekDays = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const AccountabilityTempl = ({
  athlete,
  userToLoadInformation,
  startingCalendarDay = 0,
  organizationOwner,
}) => {
  const { t } = useTranslation("coaching-portal");
  const templates =
    organizationOwner?.accountabilityTemplates?.templates ||
    userToLoadInformation?.accountabilityTemplates?.templates ||
    [];
  const defaultTemplate =
    organizationOwner?.accountabilityTemplates?.defaultTemplate ||
    userToLoadInformation?.accountabilityTemplates?.defaultTemplate ||
    [];

  const { _id: athleteId } = athlete;
  const [mobileOptions, setMobileOptions] = useState([]);

  let days = {};
  weekDays.forEach(
    (day, i) => (days = { ...days, [day]: i % 2 === 0 ? false : true })
  );

  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [idToDelete, setIdToDelete] = React.useState(undefined); //this is to save the id to delete, and also open/close the delete modal
  const assignTemplate = () => {
    if (!selectedTemplate._id)
      return alert(t("accountabilityTempl.saveTemplateFirst"));
    submit("/actions/daily-accountability/set-template", {
      method: "POST",
      body: {
        athleteId,
        templateId: selectedTemplate._id,
        userToRequest: userToLoadInformation._id,
        // if we are in an organization case, we need to send the organization owner id
        // so athletes will be taken from the coach that owns them, but
        // the templates will be taken from the organization owner
        ...(organizationOwner && { organizationOwner: organizationOwner._id }),
      },
    });
  };

  const handleSetDefault = () => {
    const actionLink =
      selectedTemplate._id === defaultTemplate
        ? "/actions/daily-accountability/remove-default"
        : "/actions/daily-accountability/set-default";

    return submit(actionLink, {
      method: "POST",
      body: {
        templateId: selectedTemplate._id,
        // when creating a template, if we are inside of an organization,
        // we will assign it to the owner of the organization
        userToRequest: organizationOwner?._id || userToLoadInformation._id,
      },
    });
  };

  const removeTemplate = (id) => {
    setIdToDelete(id); // opening the modal sending the id to delete
  };

  const handleConfirmDelete = () => {
    submit("/actions/daily-accountability/remove-template", {
      method: "POST",
      body: {
        templateId: idToDelete,
        // when creating a template, if we are inside of an organization,
        // we have to look inside the organization owner
        userToRequest: organizationOwner?._id || userToLoadInformation._id,
      },
    });
    setIdToDelete(undefined);
  };

  const handleCancelDelete = () => {
    setIdToDelete(undefined);
  };

  React.useEffect(() => {
    if (athlete.accountability?.templateId && templates) {
      const found = templates.find(
        (item) => item._id === athlete.accountability.templateId
      );
      if (found) return setSelectedTemplate(found);
    }
    if (templates && templates[0]) setSelectedTemplate(templates[0]);
    else setSelectedTemplate(null);
    const mobileOptions = templates?.map((template) => ({
      value: template._id,
      label: template.name,
    }));
    setMobileOptions(mobileOptions);
  }, [templates]);
  return (
    <Modal
      className={"accountability-template-modal"}
      title={t("accountabilityTempl.chooseTemplate")}
    >
      <div
        className={
          selectedTemplate ? "body" : "body-for-modal-no-templates body"
        }
      >
        {selectedTemplate && (
          <>
            <aside className="force-mobile-hidden">
              <p className="p5">{t("accountabilityTempl.selectTemplate")}</p>
              <ul>
                {templates?.map((item) => {
                  return (
                    <li
                      // }
                      aria-current={
                        selectedTemplate?._id === item._id ? true : undefined
                      }
                      onClick={() => setSelectedTemplate(item)}
                    >
                      <p className="p1">{item.name}</p>
                    </li>
                  );
                })}
              </ul>
            </aside>
            <section className="mobile-templates-list only-show-mobile">
              <p className="p5">{t("accountabilityTempl.selectTemplate")}</p>
              <div className="select">
                <Select
                  instanceId="header-athletes"
                  autosize={false}
                  matchPos="start"
                  clearable={false}
                  options={mobileOptions}
                  simpleValue
                  onChange={(value) => {
                    const template = templates.find(
                      (templ) => templ._id === value
                    );

                    setSelectedTemplate(template);
                  }}
                  value={selectedTemplate?.name}
                />
              </div>
            </section>
            <section>
              <header>
                <h5>{selectedTemplate.name}</h5>
                <div>
                  <button
                    onClick={() => removeTemplate(selectedTemplate._id)}
                    data-button-type="text-white"
                    data-icon-only
                    sz="medium"
                  >
                    <img
                      src="/img/new-icons/red-trash.svg"
                      alt={t("common:delete")}
                    />
                  </button>
                  <a
                    className="force-mobile-hidden"
                    href={`/actions/refresh?modal[type]=add-accountability-template&templateId=${selectedTemplate._id}`}
                    role="button"
                    data-button-type="tertiary"
                  >
                    {t("accountabilityTempl.editTemplate")}
                  </a>
                  <a
                    className="only-show-mobile"
                    href={`/actions/refresh?modal[type]=add-accountability-template&templateId=${selectedTemplate._id}`}
                    role="button"
                    data-button-type="tertiary"
                  >
                    <img
                      src="/img/new-icons/edit-pencil.svg"
                      alt={t("common:edit")}
                    />
                  </a>
                </div>
                <ConfirmationModal
                  isOpen={idToDelete !== undefined}
                  title={t("accountabilityTempl.deleteTemplateTitle")}
                  contentText={t("accountabilityTempl.deleteTemplateConfirm")}
                  cancelButtonText={t("common:cancel")}
                  cancelButtonAction={handleCancelDelete}
                  confirmButtonText={t("common:delete")}
                  confirmButtonAction={handleConfirmDelete}
                  icon="delete"
                />
              </header>
              {selectedTemplate.list.map((acc) => {
                return (
                  <article>
                    <aside>
                      <p className="s3">{acc.title}</p>
                      <p className="p5">{acc.description}</p>
                    </aside>
                    <section>
                      <SelectDays
                        days={acc.days}
                        startingCalendarDay={startingCalendarDay}
                      />
                    </section>
                  </article>
                );
              })}
            </section>
          </>
        )}
        {!selectedTemplate && (
          <div className="modal-no-templates">
            <h4>{t("accountabilityTempl.noTemplatesAvailable")}</h4>
            <p className="p5">{t("accountabilityTempl.createFirstTemplate")}</p>
            <a
              role="button"
              data-button-type="primary"
              href="/actions/refresh?modal[type]=add-accountability-template"
            >
              {t("accountabilityTempl.addNewTemplate")}
            </a>
          </div>
        )}
      </div>
      {selectedTemplate && (
        <>
          {/* footer for desktop */}
          <footer className="acc-template-footer force-mobile-hidden">
            <>
              <aside>
                <a
                  href="/actions/refresh?modal[type]=add-accountability-template"
                  role="button"
                  data-button-type="outline"
                >
                  {t("accountabilityTempl.addNewTemplate")}
                </a>
                <div
                  onClick={handleSetDefault}
                  role="button"
                  data-button-type="outline"
                >
                  <SvgIcon
                    src={`/img/new-icons/${
                      selectedTemplate?._id === defaultTemplate ? "" : "un"
                    }checked-square.svg`}
                    noFilterOnLight={true}
                  />
                  <p className="p4">{t("accountabilityTempl.setAsDefault")}</p>
                </div>
              </aside>

              <button
                onClick={selectedTemplate ? () => assignTemplate() : null}
                role="button"
                data-button-type="primary"
              >
                {t("common:save")}
              </button>
            </>
          </footer>
          {/* footer for mobile */}
          <footer className="acc-template-footer force-only-show-mobile">
            <div className="div-wrapper-footer-acc">
              <div onClick={handleSetDefault}>
                <SvgIcon
                  src={`/img/new-icons/${
                    selectedTemplate?._id === defaultTemplate ? "" : "un"
                  }checked-square.svg`}
                />
                <p className="p4">{t("accountabilityTempl.setAsDefault")}</p>
              </div>
              <div>
                <a
                  href="/actions/refresh?modal[type]=add-accountability-template"
                  role="button"
                  data-button-type="outline"
                >
                  {t("accountabilityTempl.addNewTemplate")}
                </a>
                <button
                  onClick={selectedTemplate ? () => assignTemplate() : null}
                  role="button"
                  data-button-type="primary"
                >
                  {t("common:save")}
                </button>
              </div>
            </div>
          </footer>
        </>
      )}
    </Modal>
  );
};

export default AccountabilityTempl;
